<script setup>
import { useTranslation } from 'i18next-vue';

const { t } = useTranslation();

defineProps({
    isActive: { type: Boolean, required: true },
    message: { type: String, default: '' }
});

defineEmits(['attempt-reload']);
</script>

<template>
    <div v-if="isActive" class="maintenance-overlay">
        <!-- Background decoration elements -->
        <div class="bg-grid-pattern"></div>
        <div class="bg-blob bg-blob-top"></div>
        <div class="bg-blob bg-blob-bottom"></div>
        <div class="bg-gradient-overlay"></div>

        <!-- Logo header -->
        <header class="logo-header">
            <img src="/Synapsee Logo Horz Neg.svg" alt="SynapSee Studio" class="logo-image" />
        </header>

        <!-- Main content -->
        <main class="content-wrapper">
            <!-- Maintenance card -->
            <div class="maintenance-card">
                <!-- Top gradient bar -->
                <div class="card-top-bar"></div>

                <!-- Card body -->
                <div class="card-body">
                    <!-- Icon container with floating gears -->
                    <div class="icon-container">
                        <div class="icon-circle">
                            <!-- Main Wrench Icon -->
                            <svg class="wrench-icon" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7.37141 0.469877C6.48078 -0.223873 5.21516 -0.139498 4.40891 0.657377L0.658907 4.40738C-0.137968 5.20425 -0.222343 6.46988 0.462032 7.36988L7.96203 17.1199C8.38391 17.673 9.04953 18.0011 9.74328 18.0011H14.8152L25.0339 28.2199C23.6558 30.9386 24.0964 34.3511 26.3745 36.6199L36.8745 47.1199C38.0464 48.2918 39.9495 48.2918 41.1214 47.1199L47.1214 41.1199C48.2933 39.948 48.2933 38.0449 47.1214 36.873L36.6214 26.373C34.3527 24.1043 30.9402 23.6543 28.2214 25.0324L18.0027 14.8136V9.75113C18.0027 9.048 17.6745 8.39175 17.1214 7.96988L7.37141 0.469877V0.469877M1.86828 37.1355C0.677657 38.3261 0.00265666 39.948 0.00265666 41.6355C0.00265666 45.1511 2.85266 48.0011 6.36828 48.0011C8.05578 48.0011 9.67766 47.3261 10.8683 46.1355L21.912 35.0918C21.1808 33.1324 21.0683 31.0043 21.5745 28.9886L15.7902 23.2043L1.86828 37.1355V37.1355M48.0027 13.5011C48.0027 12.5168 47.8995 11.5605 47.7027 10.6418C47.4777 9.59175 46.1933 9.31988 45.4339 10.0793L39.4433 16.0699C39.162 16.3511 38.7777 16.5105 38.3839 16.5105H33.0027C32.1777 16.5105 31.5027 15.8355 31.5027 15.0105V9.61988C31.5027 9.22613 31.662 8.84175 31.9433 8.5605L37.9339 2.56988C38.6933 1.8105 38.4214 0.526127 37.3714 0.301127C36.4433 0.104252 35.487 0.00112698 34.5027 0.00112698C27.0495 0.00112698 21.0027 6.048 21.0027 13.5011V13.5761L28.9995 21.573C32.3745 20.7199 36.1058 21.6199 38.7495 24.2636L40.2214 25.7355C44.8152 23.5793 48.0027 18.9105 48.0027 13.5011V13.5011" fill="currentColor"/>
                            </svg>
                        </div>
                        <!-- Floating Gear 1 (top-right) -->
                        <div class="floating-gear gear-1">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M23.2453 7.80937C23.3953 8.21719 23.2688 8.67187 22.9453 8.9625L20.9156 10.8094C20.9672 11.1984 20.9953 11.5969 20.9953 12C20.9953 12.4031 20.9672 12.8016 20.9156 13.1906L22.9453 15.0375C23.2688 15.3281 23.3953 15.7828 23.2453 16.1906C23.0391 16.7484 22.7906 17.2828 22.5047 17.7984L22.2844 18.1781C21.975 18.6938 21.6281 19.1813 21.2484 19.6406C20.9719 19.9781 20.5125 20.0906 20.1 19.9594L17.4891 19.1297C16.8609 19.6125 16.1672 20.0156 15.4266 20.3203L14.8406 22.9969C14.7469 23.4234 14.4187 23.7609 13.9875 23.8312C13.3406 23.9391 12.675 23.9953 11.9953 23.9953C11.3156 23.9953 10.65 23.9391 10.0031 23.8312C9.57187 23.7609 9.24375 23.4234 9.15 22.9969L8.56406 20.3203C7.82344 20.0156 7.12969 19.6125 6.50156 19.1297L3.89531 19.9641C3.48281 20.0953 3.02344 19.9781 2.74688 19.6453C2.36719 19.1859 2.02031 18.6984 1.71094 18.1828L1.49062 17.8031C1.20469 17.2875 0.95625 16.7531 0.75 16.1953C0.6 15.7875 0.726562 15.3328 1.05 15.0422L3.07969 13.1953C3.02812 12.8016 3 12.4031 3 12C3 11.5969 3.02812 11.1984 3.07969 10.8094L1.05 8.9625C0.726562 8.67187 0.6 8.21719 0.75 7.80937C0.95625 7.25156 1.20469 6.71719 1.49062 6.20156L1.71094 5.82188C2.02031 5.30625 2.36719 4.81875 2.74688 4.35938C3.02344 4.02187 3.48281 3.90938 3.89531 4.04063L6.50625 4.87031C7.13437 4.3875 7.82812 3.98438 8.56875 3.67969L9.15469 1.00312C9.24844 0.576562 9.57656 0.239063 10.0078 0.16875C10.6547 0.05625 11.3203 0 12 0C12.6797 0 13.3453 0.05625 13.9922 0.164062C14.4234 0.234375 14.7516 0.571875 14.8453 0.998438L15.4313 3.675C16.1719 3.97969 16.8656 4.38281 17.4938 4.86562L20.1047 4.03594C20.5172 3.90469 20.9766 4.02188 21.2531 4.35469C21.6328 4.81406 21.9797 5.30156 22.2891 5.81719L22.5094 6.19688C22.7953 6.7125 23.0438 7.24687 23.25 7.80469L23.2453 7.80937M12 15.75C14.0697 15.75 15.75 14.0697 15.75 12C15.75 9.93032 14.0697 8.25 12 8.25C9.93032 8.25 8.25 9.93032 8.25 12C8.25 14.0697 9.93032 15.75 12 15.75V15.75" fill="currentColor" fill-opacity="0.4"/>
                            </svg>
                        </div>
                        <!-- Floating Gear 2 (bottom-left) -->
                        <div class="floating-gear gear-2">
                            <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19.3711 6.50781C19.4961 6.84766 19.3906 7.22656 19.1211 7.46875L17.4297 9.00781C17.4727 9.33203 17.4961 9.66406 17.4961 10C17.4961 10.3359 17.4727 10.668 17.4297 10.9922L19.1211 12.5312C19.3906 12.7734 19.4961 13.1523 19.3711 13.4922C19.1992 13.957 18.9922 14.4023 18.7539 14.832L18.5703 15.1484C18.3125 15.5781 18.0234 15.9844 17.707 16.3672C17.4766 16.6484 17.0937 16.7422 16.75 16.6328L14.5742 15.9414C14.0508 16.3438 13.4727 16.6797 12.8555 16.9336L12.3672 19.1641C12.2891 19.5195 12.0156 19.8008 11.6563 19.8594C11.1172 19.9492 10.5625 19.9961 9.99609 19.9961C9.42969 19.9961 8.875 19.9492 8.33594 19.8594C7.97656 19.8008 7.70312 19.5195 7.625 19.1641L7.13672 16.9336C6.51953 16.6797 5.94141 16.3438 5.41797 15.9414L3.24609 16.6367C2.90234 16.7461 2.51953 16.6484 2.28906 16.3711C1.97266 15.9883 1.68359 15.582 1.42578 15.1523L1.24219 14.8359C1.00391 14.4062 0.796875 13.9609 0.625 13.4961C0.5 13.1562 0.605469 12.7773 0.875 12.5352L2.56641 10.9961C2.52344 10.668 2.5 10.3359 2.5 10C2.5 9.66406 2.52344 9.33203 2.56641 9.00781L0.875 7.46875C0.605469 7.22656 0.5 6.84766 0.625 6.50781C0.796875 6.04297 1.00391 5.59766 1.24219 5.16797L1.42578 4.85156C1.68359 4.42188 1.97266 4.01562 2.28906 3.63281C2.51953 3.35156 2.90234 3.25781 3.24609 3.36719L5.42188 4.05859C5.94531 3.65625 6.52344 3.32031 7.14062 3.06641L7.62891 0.835938C7.70703 0.480469 7.98047 0.199219 8.33984 0.140625C8.87891 0.046875 9.43359 0 10 0C10.5664 0 11.1211 0.046875 11.6602 0.136719C12.0195 0.195312 12.293 0.476562 12.3711 0.832031L12.8594 3.0625C13.4766 3.31641 14.0547 3.65234 14.5781 4.05469L16.7539 3.36328C17.0977 3.25391 17.4805 3.35156 17.7109 3.62891C18.0273 4.01172 18.3164 4.41797 18.5742 4.84766L18.7578 5.16406C18.9961 5.59375 19.2031 6.03906 19.375 6.50391L19.3711 6.50781M10 13.125C11.7247 13.125 13.125 11.7247 13.125 10C13.125 8.27527 11.7247 6.875 10 6.875C8.27527 6.875 6.875 8.27527 6.875 10C6.875 11.7247 8.27527 13.125 10 13.125V13.125" fill="currentColor" fill-opacity="0.3"/>
                            </svg>
                        </div>
                        <!-- Particles -->
                        <div class="particle particle-1"></div>
                        <div class="particle particle-2"></div>
                    </div>

                    <!-- Title -->
                    <h1 class="card-title">{{ t('designSystem.maintenanceOverlay.title') }}</h1>

                    <!-- Description -->
                    <p class="card-description">{{ message || t('designSystem.maintenanceOverlay.defaultMessage') }}</p>

                    <!-- Status indicator -->
                    <div class="status-indicator">
                        <div class="spinner-ring">
                            <div class="spinner-dot"></div>
                        </div>
                        <span class="status-label">{{ t('designSystem.maintenanceOverlay.updatingSystem') }}</span>
                    </div>

                    <!-- Retry button -->
                    <button class="retry-button" @click="$emit('attempt-reload')">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14.4844 7H14.75C15.1656 7 15.5 6.66562 15.5 6.25V2.25C15.5 1.94688 15.3188 1.67187 15.0375 1.55625C14.7563 1.44062 14.4344 1.50312 14.2187 1.71875L12.9187 3.01875C10.1812 0.315625 5.77187 0.325 3.05 3.05C0.315625 5.78438 0.315625 10.2156 3.05 12.95C5.78438 15.6844 10.2156 15.6844 12.95 12.95C13.3406 12.5594 13.3406 11.925 12.95 11.5344C12.5594 11.1437 11.925 11.1437 11.5344 11.5344C9.58125 13.4875 6.41562 13.4875 4.4625 11.5344C2.50937 9.58125 2.50937 6.41562 4.4625 4.4625C6.40625 2.51875 9.54688 2.50937 11.5031 4.43125L10.2187 5.71875C10.0031 5.93437 9.94063 6.25625 10.0563 6.5375C10.1719 6.81875 10.4469 7 10.75 7H14.4844V7" fill="currentColor"/>
                        </svg>
                        <span>{{ t('designSystem.maintenanceOverlay.tryAgain') }}</span>
                    </button>

                    <!-- Thanks text -->
                    <p class="thanks-text">{{ t('designSystem.maintenanceOverlay.thankYou') }}</p>
                </div>

                <!-- Card footer -->
                <div class="card-footer">
                    <div class="footer-status">
                        <span class="label">{{ t('designSystem.maintenanceOverlay.statusLabel') }}</span>
                        <span class="value">{{ t('designSystem.maintenanceOverlay.statusValue') }}</span>
                    </div>
                </div>
            </div>

            <!-- Support link -->
            <div class="support-link">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 13c3.314 0 6-1.343 6-3s-2.686-3-6-3-6 1.343-6 3 2.686 3 6 3zM1 7c0 1.657 2.686 3 6 3s6-1.343 6-3M1 7V4c0 1.657 2.686 3 6 3s6-1.343 6-3v3" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ t('designSystem.maintenanceOverlay.supportLink') }}</span>
            </div>
        </main>

        <!-- Footer -->
        <footer class="copyright">
            {{ t('designSystem.maintenanceOverlay.copyright', { year: new Date().getFullYear() }) }}
        </footer>
    </div>
</template>

<style scoped>
.maintenance-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background-color: #111827;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    font-family: 'Inter', sans-serif;
}

/* Background elements */
.bg-grid-pattern {
    position: absolute;
    inset: 0;
    background-image:
        linear-gradient(90deg, rgba(244, 63, 63, 0.05) 2.5%, rgba(244, 63, 63, 0) 2.5%),
        linear-gradient(180deg, rgba(244, 63, 63, 0.05) 2.5%, rgba(244, 63, 63, 0) 2.5%);
    background-size: 40px 40px;
    opacity: 0.1;
}

.bg-blob {
    position: absolute;
    border-radius: 9999px;
    filter: blur(50px);
}

.bg-blob-top {
    background-color: rgba(244, 63, 63, 0.1);
    width: 400px;
    height: 400px;
    top: -100px;
    left: -100px;
}

.bg-blob-bottom {
    background-color: rgba(59, 130, 246, 0.05);
    width: 300px;
    height: 300px;
    bottom: -100px;
    right: -100px;
}

.bg-gradient-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg,
        rgba(31, 41, 55, 0) 0%,
        rgba(31, 41, 55, 0.9) 50%,
        rgba(31, 41, 55, 1) 100%);
}

/* Logo header */
.logo-header {
    position: absolute;
    top: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    opacity: 0.8;
}

.logo-image {
    height: 24px;
    width: auto;
}

/* Content wrapper */
.content-wrapper {
    position: relative;
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 32px;
    padding: 132px 16px 0;
}

/* Maintenance card */
.maintenance-card {
    background: var(--color-surface);
    border-radius: var(--radius-xl);
    overflow: hidden;
    box-shadow: var(--shadow-xl);
    width: 100%;
    max-width: 512px;
}

.card-top-bar {
    height: 8px;
    background: linear-gradient(to right, var(--color-primary), var(--color-warning));
}

.card-body {
    padding: 48px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 24px;
    min-height: 440px;
    position: relative;
}

/* Icon */
.icon-container {
    position: relative;
    width: 128px;
    height: 128px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
}

.icon-circle {
    position: absolute;
    width: 128px;
    height: 128px;
    border-radius: 9999px;
    background-color: var(--color-error-light);
    display: flex;
    align-items: center;
    justify-content: center;
}

.wrench-icon {
    width: 48px;
    height: 48px;
    color: var(--color-primary);
    filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.06)) drop-shadow(0px 4px 3px rgba(0, 0, 0, 0.07));
}

.floating-gear {
    position: absolute;
    animation: float 3s ease-in-out infinite;
}

.floating-gear svg {
    display: block;
    width: 100%;
    height: 100%;
    color: var(--color-primary);
}

.gear-1 {
    width: 24px;
    height: 24px;
    top: 24px;
    right: 24px;
    animation-delay: 0s;
}

.gear-2 {
    width: 20px;
    height: 20px;
    bottom: 32px;
    left: 24px;
    animation-delay: 1.5s;
}

.particle {
    position: absolute;
    border-radius: 9999px;
    animation: float 2s ease-in-out infinite;
}

.particle-1 {
    width: 8px;
    height: 8px;
    background-color: var(--color-accent);
    top: 0;
    right: 40px;
}

.particle-2 {
    width: 6px;
    height: 6px;
    background-color: var(--color-secondary);
    bottom: 16px;
    right: 40px;
    animation-delay: 0.5s;
}

/* Title */
.card-title {
    font-size: 30px;
    font-weight: 700;
    color: var(--color-text-primary);
    line-height: 36px;
    letter-spacing: -0.75px;
    margin: 0;
}

/* Description */
.card-description {
    font-size: 16px;
    font-weight: 400;
    color: var(--color-text-secondary);
    line-height: 26px;
    margin: 0;
    max-width: 384px;
}

/* Status indicator */
.status-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 16px 0;
}

.spinner-ring {
    position: relative;
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.spinner-ring::before {
    content: '';
    position: absolute;
    width: 56px;
    height: 56px;
    border: 4px solid var(--color-border-subtle);
    border-radius: 9999px;
}

.spinner-ring::after {
    content: '';
    position: absolute;
    width: 48px;
    height: 48px;
    border: 4px solid transparent;
    border-top-color: var(--color-primary);
    border-right-color: var(--color-primary);
    border-radius: 9999px;
    animation: spin 1.5s linear infinite;
}

.spinner-dot {
    position: relative;
    width: 100%;
    height: 100%;
}

.status-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-tertiary);
    letter-spacing: 1.2px;
    text-transform: uppercase;
}

/* Retry button */
.retry-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 32px;
    border: 1px solid var(--color-primary);
    background-color: var(--color-surface);
    border-radius: var(--radius-md);
    color: var(--color-primary);
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
}

.retry-button:hover {
    background-color: var(--color-primary);
    color: var(--color-surface);
}

.retry-button svg {
    width: 16px;
    height: 16px;
}

/* Thanks text */
.thanks-text {
    font-size: 12px;
    font-weight: 400;
    color: var(--color-text-tertiary);
    margin: 0;
}

/* Card footer */
.card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 12px 24px;
    border-top: 1px solid var(--color-border-subtle);
    background-color: var(--color-background-neutral);
}

.footer-status {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: var(--font-size-label-small, 12px);
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--color-text-tertiary);
}

.footer-status .value {
    color: var(--color-warning);
}

/* Support link */
.support-link {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
    color: var(--color-text-tertiary);
    font-size: 14px;
    font-weight: 400;
}

.support-link svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
}

/* Copyright */
.copyright {
    position: absolute;
    bottom: 24px;
    left: 0;
    right: 0;
    text-align: center;
    font-size: 12px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.4);
}

/* Animations */
@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

@keyframes float {
    0%, 100% {
        transform: translateY(0px);
    }
    50% {
        transform: translateY(-8px);
    }
}

/* SYN-2738 (A14): engrenagens/partículas são decorativas — param com
   prefers-reduced-motion; o spinner de status só desacelera. */
@media (prefers-reduced-motion: reduce) {
    .floating-gear,
    .particle {
        animation: none;
    }
    .spinner-ring::after {
        animation-duration: 3s;
    }
}
</style>
